import { useQuery } from '@tanstack/react-query';
import { Skeleton, Tag, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import type { ConnectivityStatusMap, LastSeenMap } from '@/service/vpc/queryOptions';
import type { IConnectivityStatus } from '@/service/vpc/types/IConnectivityStatus';
import type { IChassisIdentifier, IUnit } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';

import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { getLatestOperatingHoursQueryOptions } from '@/service/vpc/queryOptions';
import { chassis, dates } from '@/utils';
import { getConnectivityStatusVariant } from '@/utils/telemetry';

export const mapConnectivityTableData = (
  lastSeenData: LastSeenMap | undefined,
  connectivityStatusData: ConnectivityStatusMap | undefined,
  units: IUnit[] | undefined
) => {
  if (!lastSeenData) return [];

  return Object.values(lastSeenData).map((item) => ({
    unit: units?.find((unit) => unit.endCustomerUnitId === item.endCustomerUnitId),
    lastSeen: item.lastseen ?? 'N/A',
    connectivityStatus: connectivityStatusData?.[item.endCustomerUnitId],
  }));
};

const useConnectivityTable = () => {
  const { t } = useTranslation();
  // const { mediaQueryNodes: mq } = useMediaQuery();
  const { fleetVerifiedUnits } = useFleetAccount();

  const endCustomerUnitIds = fleetVerifiedUnits?.flatMap((unit) => unit.endCustomerUnitId) || [];

  const { data: latestOperatingHours, isLoading: isLoadingHours } = useQuery(
    getLatestOperatingHoursQueryOptions({ endcustomerunitids: endCustomerUnitIds })
  );

  const getOperatingHoursForChassisId = (ci: IChassisIdentifier) => {
    const latestKnownOperationHours = latestOperatingHours?.find(
      (data) => data.ChassisId === `${ci.chassisSeries}${ci.chassisNumber}`
    );
    return latestKnownOperationHours;
  };

  const columns = [
    {
      accessorFn: (row) => row.unit?.unitName,
      id: 'unitName',
      header: t(($) => $.components.table.connectivity_table.unit),
      cell: (info) => {
        return <Typography>{info.getValue<string>()}</Typography>;
      },
    },
    {
      accessorKey: 'operatinghours',
      id: 'operatinghours',
      header: t(($) => $.components.table.connectivity_table.operating_hours),
      sortingFn: (a, b) => {
        const getTotalHours = (chassisIds: IChassisIdentifier[] | undefined) => {
          if (!chassisIds) return 0;
          return chassisIds.reduce((acc, ci) => {
            const op = getOperatingHoursForChassisId(ci);
            return acc + (op?.Value || 0);
          }, 0);
        };

        const chassisIdsA = a.original.unit ? chassis.getUnitsChassisIdentifier(a.original.unit) : [];
        const chassisIdsB = b.original.unit ? chassis.getUnitsChassisIdentifier(b.original.unit) : [];
        const totalHoursA = getTotalHours(chassisIdsA);
        const totalHoursB = getTotalHours(chassisIdsB);
        return totalHoursB - totalHoursA;
      },
      cell: (info) => {
        if (isLoadingHours) {
          return <Skeleton variant="rounded-rectangle" height={18} width={100} />;
        }
        const unitsChassisIds = chassis.getUnitsChassisIdentifier(info.row.original.unit);
        const operatingHours = unitsChassisIds.map((id) => getOperatingHoursForChassisId(id));
        const maxHours = Math.max(...unitsChassisIds.map((id) => getOperatingHoursForChassisId(id)?.Value || 0));
        const date = operatingHours.map((op) => op?.Date);

        return (
          <div className="flex flex-col">
            <Typography variant="caption1" className="text-subtle">
              {Math.round(maxHours).toLocaleString()} h
            </Typography>
            <Typography variant="caption2" className="text-subtle">
              {dates.formatDateTime(date[0])}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.lastSeen,
      id: 'lastSeen',
      header: t(($) => $.components.table.connectivity_table.last_seen),
      sortingFn: (a, b) => {
        const lastSeenA = a.original.lastSeen;
        const lastSeenB = b.original.lastSeen;

        // Handle N/A values - push them to the end
        if (lastSeenA === 'N/A' && lastSeenB === 'N/A') return 0;
        if (lastSeenA === 'N/A') return 1;
        if (lastSeenB === 'N/A') return -1;

        // Compare dates - most recent first (descending)
        const dateA = new Date(lastSeenA).getTime();
        const dateB = new Date(lastSeenB).getTime();
        return dateB - dateA;
      },
      cell: (info) => {
        const value = info.getValue<string>();
        const date = dates.formatDateTimeSince(value);
        return <Typography>{date}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.connectivityStatus?.status,
      id: 'status_connectivity',
      header: t(($) => $.components.table.connectivity_table.status),
      cell: (info) => {
        const variant = getConnectivityStatusVariant(info.getValue<string | null | undefined>());

        if (!variant) return <Typography>-</Typography>;

        return (
          <Tag variant={variant === 'success' ? 'success-subtle' : 'neutral-subtle'}>
            {variant === 'success' ? 'Online' : 'Offline'}
          </Tag>
        );
      },
    },
  ] as ColumnDef<{
    unit: IUnit | undefined;
    lastSeen: string;
    connectivityStatus: IConnectivityStatus | undefined;
  }>[];

  return { columns };
};
export default useConnectivityTable;
